import styled from 'styled-components';
import { motion } from 'framer-motion';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  padding-top: 80px;
  position: relative;
  overflow: hidden;
  background-color: #9ca3af;

  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

export const HeroImageContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

export const HeroImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
`;

export const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgb(0 0 0 / 20%), rgb(0 0 0 / 20%));
  z-index: 1;
`;

export const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  padding-top: 34vh;
  padding-bottom: 40px;
  position: relative;
  z-index: 2;
  padding-left: 40px;
  padding-right: 40px;

  @media (max-width: 1024px) {
    padding-top: 25vh;
  }

  @media (max-width: 768px) {
    padding-top: 15vh;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 20px;
  }
`;

export const TopRow = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    margin-bottom: 10px;
  }
`;

export const TitleColumn = styled(motion.div)`
  flex-basis: 66.66%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

export const TitleWrapper = styled.div`
  flex-basis: 80%;
`;

export const Title = styled(motion.h1)`
  font-size: 64px;
  font-weight: 600;
  font-style: normal;
  color: #fff;
  line-height: 1.1;
  letter-spacing: -0.03em;
  text-transform: none;
  font-family: ${fonts.headline};
  margin: 0;
  margin-bottom: 0;

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 32px;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

export const ImageColumn = styled(motion.div)`
  flex-basis: 33.33%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ImageWrapper = styled(motion.div)`
  width: 100%;
  max-width: 600px;
  position: relative;
  z-index: 3;

  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

export const Image = styled.img`
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3));
`;

export const BottomRow = styled.div`
  display: flex;
  gap: 40px;
  color: ${colors.porcelain};
  margin-top: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

export const LeftInfoColumn = styled(motion.div)`
  flex-basis: 66.66%;
  border-top: 1px solid rgba(240, 244, 239, 0.3);
  padding-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    flex-basis: 100%;
    padding-top: 20px;
  }
`;

export const DateText = styled(motion.p)`
  font-size: 16px;
  color: ${colors.porcelain};
  opacity: 0.9;
  margin: 0;
  font-weight: 500;
  font-family: ${fonts.body};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const SquareMeters = styled(motion.p)`
  font-size: 16px;
  color: ${colors.porcelain};
  opacity: 0.9;
  margin: 0;
  font-weight: 500;
  font-family: ${fonts.body};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export const RightInfoColumn = styled(motion.div)`
  flex-basis: 33.33%;
  border-top: 1px solid rgba(240, 244, 239, 0.3);
  padding-top: 40px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 768px) {
    flex-basis: 100%;
    padding-top: 20px;
    align-items: flex-start;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

export const BuyButton = styled(motion.a)`
  padding: 10px 24px;
  background: transparent;
  border: 1.5px solid ${colors.porcelain};
  color: ${colors.porcelain};
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  font-family: ${fonts.body};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 50px;

  &:hover {
    background: ${colors.porcelain};
    color: ${colors.deepCharcoal};
    border-color: ${colors.porcelain};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
  }
`;

export const DownloadButton = styled(BuyButton)`
  background: ${colors.porcelain};
  color: ${colors.deepCharcoal};

  &:hover {
    background: transparent;
    color: ${colors.porcelain};
    border-color: ${colors.porcelain};
  }
`;
